import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import KnowledgeModal from "../components/Knowledge/KnowledgeModal";
import { usePublicRates, rateOr, demandRateOr } from "../hooks/usePublicRates";
import { usePwaInstall } from "../hooks/usePwaInstall";
import PwaInstallPrompt from "../components/PwaInstallPrompt/PwaInstallPrompt";
import LanguageSelector from "../components/LanguageSelector/LanguageSelector";
import KredafiLogo from "./KredafiLogo";
import { KREDAFI_I18N, KREDAFI_LANGS } from "./landingI18n";
import "./kredafi.css";

interface Props {
  onEnter: () => void;
}

const TERMS = [3, 7, 10, 14, 30, 90];
const NETS = [
  { id: "ton", name: "TON", color: "#0098ea" },
  { id: "polygon", name: "Polygon", color: "#8247e5" },
  { id: "bsc", name: "BNB", color: "#f0b90b" },
  { id: "ethereum", name: "Ethereum", color: "#627eea" },
  { id: "solana", name: "Solana", color: "#0f1115" },
  { id: "tron", name: "TRON", color: "#e50915" },
];

function tr(ru: string, code: string): string {
  if (code === "ru") return ru;
  return (KREDAFI_I18N[ru] && KREDAFI_I18N[ru][code]) || ru;
}

const KredafiLanding: React.FC<Props> = ({ onEnter }) => {
  const { i18n } = useTranslation();
  const rawLang = (
    i18n.language ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("language") || localStorage.getItem("kredafi-lang")
      : null) ||
    "en"
  )
    .split("-")[0]
    .toLowerCase();
  const lang = KREDAFI_LANGS.some((l) => l.code === rawLang)
    ? rawLang
    : rawLang === "ru"
    ? "ru"
    : "en";
  const meta = KREDAFI_LANGS.find((l) => l.code === lang) || KREDAFI_LANGS[0];
  const t = (ru: string) => tr(ru, lang);

  const [amount, setAmount] = useState(1000);
  const [days, setDays] = useState(10);
  const [calcTab, setCalcTab] = useState<"overnight" | "demand">("overnight");
  const [marginLeverage, setMarginLeverage] = useState<0 | 1 | 2 | 3>(0);
  const [marginOpen, setMarginOpen] = useState(false);
  const [knowledgeId, setKnowledgeId] = useState<string | null>(null);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const { isInstalled: pwaInstalled } = usePwaInstall();

  // Ставки берём из админки (project_settings) через публичный эндпоинт.
  // Значения справа — запасные: лендинг открывается и при недоступном API.
  const publicRates = usePublicRates();
  const overnightRate = rateOr(publicRates, "max_rate_overnight", 0.3);
  const demandRate = demandRateOr(publicRates, overnightRate);
  const marginRate = rateOr(publicRates, "marginal_rate_overnight", 0.15);
  const num = (n: number) =>
    (Math.round(n * 100) / 100).toString().replace(".", lang === "ru" ? "," : ".");

  const openDoc = (id?: string) => {
    setKnowledgeId(id || null);
    setKnowledgeOpen(true);
  };

  const calcData = useMemo(() => {
    const rate = calcTab === "demand" ? demandRate : overnightRate;
    const marginAmount = amount * marginLeverage;
    const totalActive = amount + marginAmount;
    const grossProfit = totalActive * (rate / 100) * days;
    const poolFee = grossProfit * 0.25; // 25% с валовой прибыли в Страховой пул
    const afterPool = grossProfit - poolFee;
    const marginCost = marginAmount * (marginRate / 100) * days;
    const netProfit = afterPool - marginCost;
    const totalReturn = amount + netProfit;

    return {
      rate,
      marginAmount,
      totalActive,
      grossProfit,
      poolFee,
      marginCost,
      netProfit,
      totalReturn,
    };
  }, [amount, days, calcTab, marginLeverage, overnightRate, demandRate, marginRate]);

  const money = (n: number) =>
    "$" +
    Math.round(n)
      .toLocaleString(lang === "ru" ? "ru-RU" : "en-US")
      .replace(/\u00a0/g, " ");

  const landClass = pwaInstalled ? "kf-land kf-land--no-sticky" : "kf-land";

  return (
    <div className={landClass} dir={meta.rtl ? "rtl" : "ltr"}>
      <div className="kf-topline" />
      <div className="kf-wrap">
        <header className="kf-land-header">
          <KredafiLogo size={52} opticalAlign />
          <nav className="kf-nav">
            <a href="#tools">{t("Продукты")}</a>
            <a href="#calc">{t("Ставки")}</a>
            <a href="#tools">{t("Как это работает")}</a>
            <a href="#tools">{t("Партнёрам")}</a>
            <button
              type="button"
              className="kf-login"
              onClick={() => openDoc("history")}
            >
              {t("Документация")}
            </button>
          </nav>
          <div className="kf-hd-right">
            <LanguageSelector theme="light" compact={true} />
            <button type="button" className="kf-login" onClick={onEnter}>
              {t("Войти")}
            </button>
            <button type="button" className="kf-btn-dark" onClick={onEnter}>
              {t("Открыть приложение →")}
            </button>
          </div>
        </header>

        <section className="kf-hero">
          <div className="kf-hero-copy">
            <h1>{t("Кошелёк, где деньги зарабатывают каждую ночь.")}</h1>
            <p className="kf-eyebrow kf-hero-sub">
              {t("Доходный мультичейн-кошелёк · ночь и до востребования")}
            </p>
            <p className="kf-lead">
              {t(
                "Кладите крипту на полку: на ночь или до востребования. USDT, USDC и нативы шести сетей. Деньги зарабатывают, пока вы спите."
              )}
            </p>
            <div className="kf-cta">
              <button
                type="button"
                className="primary"
                onClick={() => {
                  try {
                    sessionStorage.setItem("kf_after_login", "deposit");
                  } catch {
                    /* private mode */
                  }
                  onEnter();
                }}
              >
                {t("Пополнить")}
              </button>
              <a className="ghost" href="#calc">
                {t("Разместить под текущую ставку")}
              </a>
            </div>
            <div className="kf-stats">
              <div className="kf-stat-item">
                <strong className="kf-stat-value">
                  ~{num(overnightRate)}%
                </strong>
                <span className="kf-stat-label">{t("ночь, плавающая")}</span>
              </div>
              <div className="kf-stat-item">
                <strong className="kf-stat-value">
                  ~{num(demandRate)}%
                </strong>
                <span className="kf-stat-label">{t("до востребования, чуть выше")}</span>
              </div>
              <div className="kf-stat-item">
                <strong className="kf-stat-value">6</strong>
                <span className="kf-stat-label">{t("сетей")}</span>
              </div>
            </div>
          </div>

          <div className="kf-phone" id="calc">
            <div className="kf-island" />
            <div className="kf-phone-screen">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "14px 26px 6px",
                  font: "500 13px Geist, sans-serif",
                }}
              >
                <b>9:41</b>
                <span className="kf-mono" style={{ color: "#93a0b3", fontSize: 11 }}>
                  5G ▮
                </span>
              </div>
              <div className="kf-calc-tabs" role="tablist">
                {(
                  [
                    ["overnight", "На ночь"],
                    ["demand", "До востребования"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={calcTab === id}
                    onClick={() => setCalcTab(id)}
                  >
                    {t(label)}
                  </button>
                ))}
              </div>
              <div className="kf-calc-body">
                <div className="kf-calc-label">{t("Сумма размещения")}</div>
                <div className="kf-calc-amount">
                  <span>{money(amount)}</span>
                  <span style={{ color: "#93a0b3" }}>USDT</span>
                </div>
                <input
                  type="range"
                  min={100}
                  max={25000}
                  step={100}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  style={{ width: "100%", margin: "14px 0 16px", accentColor: "#0fc1be" }}
                />
                <div className="kf-terms">
                  {TERMS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={days === d}
                      onClick={() => setDays(d)}
                    >
                      {d}
                      {t(" дн.")}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="kf-adv-toggle"
                  aria-expanded={marginOpen}
                  onClick={() => setMarginOpen((open) => !open)}
                >
                  {t("Дополнительно: плечо")}
                  <span>{marginOpen ? "▴" : "▾"}</span>
                </button>
                {marginOpen && (
                  <div className="kf-margin-box">
                    <div className="kf-margin-label">
                      <span>{t("Маржинальный заём (плечо)")}</span>
                      <span className="kf-margin-rate">
                        {num(marginRate)}{t("%/сут")}
                      </span>
                    </div>
                    <div className="kf-margin-options">
                      {[
                        { val: 0, label: t("Без плеча") },
                        { val: 1, label: "+100%" },
                        { val: 2, label: "+200%" },
                        { val: 3, label: "+300%" },
                      ].map((opt) => (
                        <button
                          key={opt.val}
                          type="button"
                          aria-pressed={marginLeverage === opt.val}
                          onClick={() => setMarginLeverage(opt.val as 0 | 1 | 2 | 3)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="kf-margin-warn">
                      {t(
                        "Заёмная часть не страхуется — риск как у обычной маржи."
                      )}
                    </p>
                  </div>
                )}

                <div className="kf-row">
                  <span>{t("Ставка")}</span>
                  <span className="v kf-rate-live">
                    <span>
                      {calcData.rate.toFixed(2).replace(".", ",")}
                      {t("% / сутки")}
                    </span>
                    <span className="kf-rate-tag">{t("live · плавающая")}</span>
                  </span>
                </div>

                {marginLeverage > 0 && (
                  <>
                    <div className="kf-row">
                      <span>{t("Объём размещения")}</span>
                      <span className="v">{money(calcData.totalActive)} USDT</span>
                    </div>
                    <div className="kf-row">
                      <span>{t("Страховой пул (25%)")}</span>
                      <span className="v neg">-{money(calcData.poolFee)}</span>
                    </div>
                    <div className="kf-row">
                      <span>
                        {t("Маржинальный заём")} ({num(marginRate)}%)
                      </span>
                      <span className="v neg">-{money(calcData.marginCost)}</span>
                    </div>
                  </>
                )}
                <div className="kf-row">
                  <span>{t("Чистый доход")}</span>
                  <span className="v pos">+{money(calcData.netProfit)}</span>
                </div>
                <div className="kf-row-total">
                  <span>{t("К возврату")}</span>
                  <span>{money(calcData.totalReturn)}</span>
                </div>
                <button
                  type="button"
                  className="kf-btn-teal"
                  onClick={() => {
                    try {
                      sessionStorage.setItem(
                        "kf_after_login",
                        calcTab === "demand" ? "issue-demand" : "issue"
                      );
                    } catch {
                      /* private mode */
                    }
                    onEnter();
                  }}
                >
                  {t("Разместить под текущую ставку")}
                </button>
                <div className="kf-fine">
                  {marginLeverage > 0
                    ? t("Заёмная часть без страховки.")
                    : calcTab === "demand"
                    ? t("Стоп возвращает средства на рабочий баланс.")
                    : t("Возврат утром.")}{" "}
                  <button
                    type="button"
                    className="kf-fine-link"
                    onClick={() => openDoc("anti-bankrun")}
                  >
                    {t("Условия вывода")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="kf-wrap">
        <section className="kf-nets">
          <div>
            <div className="kf-cap">{t("Расчёты в сетях")}</div>
            <p>
              {t(
                "Один счёт, шесть сетей. Пополнение и вывод в любой из них — единая плавающая ставка по рынку."
              )}
            </p>
          </div>
          <div className="kf-netlist">
            {NETS.map((n) => (
              <div key={n.id} className="kf-net">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: n.color,
                    display: "inline-block",
                  }}
                />
                {n.name}
              </div>
            ))}
          </div>
        </section>

        <section className="kf-tools" id="tools">
          <div className="kf-tools-hd">
            <h2>{t("Три полки одного кошелька")}</h2>
            <button
              type="button"
              className="kf-login"
              onClick={() => openDoc()}
            >
              {t("Вся документация →")}
            </button>
          </div>
          <div className="kf-tools-grid">
            {[
              {
                n: "01",
                title: "Доступно",
                body: "Рабочий баланс на платформе: пополняйте, размещайте под текущую ставку или выводите.",
                link: "Как устроен кошелёк →",
                article: "about",
              },
              {
                n: "02",
                title: "На ночь",
                body: "Свободные средства до утра по плавающей ставке. К утру — обратно на рабочий баланс.",
                link: "Подробнее об овернайт →",
                article: "overnight",
              },
              {
                n: "03",
                title: "До востребования",
                body: "Ставка чуть выше ночи. Стоп — возврат на рабочий баланс.",
                link: "Подробнее о займах до востребования →",
                article: "demand_pool",
              },
              {
                n: "04",
                title: "Партнёрская программа",
                body: "10% от прибыли первой линии и 2% второй. Разовый 5% от вклада приглашённого.",
                link: "Условия партнерской программы →",
                article: "affiliates",
              },
            ].map((tool) => (
              <button
                type="button"
                className="kf-tool"
                key={tool.n}
                onClick={() => openDoc(tool.article)}
              >
                <div className="n">{tool.n}</div>
                <h3>{t(tool.title)}</h3>
                <p>{t(tool.body)}</p>
                <span className="lk">{t(tool.link)}</span>
              </button>
            ))}
          </div>
        </section>

        <footer className="kf-land-footer">
          <div className="kf-footer-inner">
            <div className="kf-footer-grid">
              <div className="kf-footer-brand-col">
                <KredafiLogo size={52} opticalAlign />
                <p className="kf-footer-desc">
                  {t(
                    "Доходный мультичейн-кошелёк: деньги не лежат мёртвым грузом, а зарабатывают каждую ночь. Полки «на ночь» и «до востребования» в сетях TON, Polygon, Solana, TRON, BNB Chain и Ethereum."
                  )}
                </p>
                <div className="kf-footer-shield">
                  {t("Полки «на ночь» и «до востребования» в шести сетях.")}
                </div>
              </div>

              {[
                [
                  { id: "overnight", label: "Ночные займы" },
                  { id: "demand_pool", label: "До востребования" },
                  { id: "margin-loans", label: "Маржинальные займы" },
                  { id: "unsecured", label: "Необеспеченные займы" },
                  { id: "certificates", label: "Кредитные сертификаты" },
                ],
                [
                  { id: "insurance", label: "Страховой пул" },
                  { id: "anti-bankrun", label: "Лимит вывода" },
                  { id: "affiliates", label: "Партнерская программа" },
                  { id: "liquidity-pools", label: "Пулы ликвидности" },
                  {
                    id: "about",
                    label: "Доходный мультичейн-кошелёк",
                  },
                ],
              ].map((column, colIndex) => (
                <div className="kf-footer-col" key={colIndex}>
                  <ul className="kf-footer-links">
                    {column.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          className="kf-footer-link"
                          onClick={() => openDoc(item.id)}
                        >
                          {t(item.label)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="kf-footer-bottom">
              <div className="kf-footer-copy">
                <div>
                  © {new Date().getFullYear()} KredaFi. {t("Все права защищены.")}
                </div>
                <div className="kf-footer-beta-desc">
                  {t("Платформа работает в режиме публичной бета-версии")}
                </div>
              </div>
              <div className="kf-footer-bottom-right">
                <span className="kf-beta-pill">
                  <span className="kf-beta-dot" />
                  <span>{t("Бета-версия")}</span>
                </span>
                <div className="kf-footer-pwa">
                  <PwaInstallPrompt theme="light" />
                </div>
                <div className="kf-footer-legal">
                  <a href="/privacy.html" className="kf-footer-link kf-footer-link-ext">
                    {t("Privacy Policy")}
                  </a>
                  <span aria-hidden="true">·</span>
                  <a href="/terms.html" className="kf-footer-link kf-footer-link-ext">
                    {t("Terms of Service")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {!pwaInstalled && !knowledgeOpen && (
        <div className="kf-sticky-cta">
          <button type="button" className="a kf-sticky-cta-single" onClick={onEnter}>
            {t("Открыть приложение")}
          </button>
        </div>
      )}

      <KnowledgeModal
        isOpen={knowledgeOpen}
        onClose={() => setKnowledgeOpen(false)}
        initialArticleId={knowledgeId}
        theme="light"
      />
    </div>
  );
};

export default KredafiLanding;
